import { useEffect, useRef, useCallback } from 'react';
import { trackView } from "../Data/notificationStatsQueue";

export default function useVisibleNotificationsBatch({ 
    userId, 
    notifications, 
    onBatchSend 
}) {
    const containerRef = useRef(null);
    const observerRef = useRef(null);
    const processedViewIds = useRef(new Set());
    const processedReadIds = useRef(new Set());
    const batchTimeoutRef = useRef(null);
    const pendingBatch = useRef(new Set());
    const isInitialized = useRef(false);

    const processBatch = useCallback(() => {
        if (pendingBatch.current.size === 0) return;
        
        const idsToSend = Array.from(pendingBatch.current);
        pendingBatch.current.clear();
        
        console.log('📤 Marking as read batch:', idsToSend);
        onBatchSend(idsToSend, userId);
    }, [onBatchSend, userId]);

    const processNotificationInView = useCallback((notificationId, notification) => {
        console.log('👁️ Processing notification in view:', notificationId);

        if (!processedViewIds.current.has(notificationId)) {
            console.log('📊 Tracking view for:', notificationId);
            trackView(notificationId);
            processedViewIds.current.add(notificationId);
        }

        const isRead = notification.readStatus?.isRead;
        const element = document.querySelector(`[data-notification-id="${notificationId}"]`);
        const isTruncated = element?.getAttribute('data-truncated') === 'true';

        if (!isRead && !isTruncated && !processedReadIds.current.has(notificationId)) {
            console.log('✅ Adding to read batch:', notificationId);
            pendingBatch.current.add(notificationId);
            processedReadIds.current.add(notificationId);
            
            if (batchTimeoutRef.current) {
                clearTimeout(batchTimeoutRef.current);
            }
            batchTimeoutRef.current = setTimeout(processBatch, 1000);
        }
    }, [processBatch]);

    const checkVisibleNotifications = useCallback(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();
        
        notifications.forEach(notification => {
            const element = container.querySelector(`[data-notification-id="${notification.id}"]`);
            if (!element) return;

            const elementRect = element.getBoundingClientRect();
            
            const isVisible = (
                elementRect.top < containerRect.bottom &&
                elementRect.bottom > containerRect.top &&
                elementRect.left < containerRect.right &&
                elementRect.right > containerRect.left
            );

            const visibleTop = Math.max(elementRect.top, containerRect.top);
            const visibleBottom = Math.min(elementRect.bottom, containerRect.bottom);
            const visibleHeight = Math.max(0, visibleBottom - visibleTop);
            const elementHeight = elementRect.height;
            const visibilityRatio = elementHeight > 0 ? visibleHeight / elementHeight : 0;

            if (isVisible && visibilityRatio >= 0.6) {
                processNotificationInView(notification.id, notification);
            }
        });
    }, [notifications, processNotificationInView]);

    useEffect(() => {
        if (!containerRef.current || !userId || notifications.length === 0) return;

        console.log('🔄 Setting up notification tracking...');

        if (observerRef.current) {
            observerRef.current.disconnect();
        }
        if (batchTimeoutRef.current) {
            clearTimeout(batchTimeoutRef.current);
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const element = entry.target;
                    const notificationId = element.getAttribute('data-notification-id');
                    
                    if (!notificationId) return;

                    const notification = notifications.find(n => n.id === notificationId);
                    if (!notification) return;

                    if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                        processNotificationInView(notificationId, notification);
                    }
                });
            },
            {
                root: containerRef.current,
                threshold: [0, 0.25, 0.5, 0.6, 0.75, 1.0],
                rootMargin: '10px'
            }
        );

        const setupTimeout = setTimeout(() => {
            const container = containerRef.current;
            if (!container) return;

            const notificationElements = container.querySelectorAll('[data-notification-id]');
            console.log('🎯 Found notification elements:', notificationElements.length);
            
            notificationElements.forEach(el => {
                observerRef.current.observe(el);
            });

            checkVisibleNotifications();
            isInitialized.current = true;
        }, 100);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (batchTimeoutRef.current) {
                clearTimeout(batchTimeoutRef.current);
            }
            clearTimeout(setupTimeout);
        };
    }, [userId, notifications, processNotificationInView, checkVisibleNotifications]);

    // Add scroll listener for additional checks
    useEffect(() => {
        if (!containerRef.current || !isInitialized.current) return;

        const container = containerRef.current;
        let scrollTimeout;

        const handleScroll = () => {
            // Debounce scroll checks
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                checkVisibleNotifications();
            }, 150);
        };

        container.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            container.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, [checkVisibleNotifications, isInitialized.current]);

    // Clean up processed IDs when notifications change
    useEffect(() => {
        const currentIds = new Set(notifications.map(n => n.id));
        
        // Clean up view tracking
        Array.from(processedViewIds.current).forEach(id => {
            if (!currentIds.has(id)) {
                processedViewIds.current.delete(id);
            }
        });
        
        // Clean up read tracking
        Array.from(processedReadIds.current).forEach(id => {
            if (!currentIds.has(id)) {
                processedReadIds.current.delete(id);
            }
        });
    }, [notifications]);

    return { containerRef };
}
